import { useMemo } from 'react';
import {
	ENUM_UTILS,
	HIDDEN_PERP_MARKET_INDEXES,
} from '@velocity-exchange/common';
import { MarketType } from '@velocity-exchange/sdk';
import { ZERO } from 'src/constants/math';
import { useCurrentAccountPositions } from './query/accountTradingData/usePositions';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

/**
 * Returns a Set of perp market indexes that should be hidden from the current user.
 * All markets in HIDDEN_PERP_MARKET_INDEXES are hidden unless the user has
 * active positions, orders, or unsettled PnL in that market.
 */
export default function useHiddenPerpMarkets(): ReadonlySet<number> {
	const openPerpPositions = useCurrentAccountPositions();
	const openOrders = useCurrentAccountOrders();

	return useMemo(() => {
		const hiddenSet = new Set<number>();

		for (const [marketIndex] of HIDDEN_PERP_MARKET_INDEXES) {
			const hasPositionOrUnsettledPnl = openPerpPositions?.some(
				(pos) =>
					pos.marketIndex === marketIndex &&
					(!pos.baseSize.eq(ZERO) || !pos.unsettledPnl.eq(ZERO))
			);

			const hasOrder = openOrders?.some(
				(order) =>
					order.marketIndex === marketIndex &&
					ENUM_UTILS.match(order.marketType, MarketType.PERP)
			);

			if (!hasPositionOrUnsettledPnl && !hasOrder) {
				hiddenSet.add(marketIndex);
			}
		}

		return hiddenSet;
	}, [openPerpPositions, openOrders]);
}
